//types

import { BlocksContent } from "@strapi/blocks-react-renderer";

export type StrapiResponse<T> = {
  data: T;
  message: string;
  meta?: PaginationMeta;
};

//full app model
export type AppResponse = {
  id: number;
  attributes: AppDetails;
};

//custom app model
export type AllAppDetails = {
  app: AppDetails;
  category: iCategoryList;
  subcategory?: iSubcategory;
};

export type liteAppDetails = {
  appExcerpt: string;
  appId: string;
  appName: string;
  appSlug: string;
  appThumbnail: {
    id: number;
    name: string;
    alternativeText: string | null;
    url: string;
    ext: string;
    hash: string;
  };
  catId: number;
  subCatId: number;
  id: number;
  downloadNumber?: number;
};

export interface PaginationMeta {
  pagination: {
    page: number;
    pageCount: number;
    pageSize: number;
    total: number;
  };
}

export type AppDetails = {
  createdAt: Date;
  updatedAt: Date;
  publishedAt?: Date;
  appName: string;
  appSlug: string;
  appDisplayName: string;
  downloadNumber?: number;
  appImages?: string;
  appExcerpt: string;
  editorPicked?: boolean;
  appId: string;
  appDevId?: number;
  appLastUpdated?: Date;
  appTotalVotes?: number;
  appTotalRating?: number;
  appOldCategory?: string;
  appRecommendFrom?: Date;
  appRecommendTill?: Date;
  appCoolFrom?: Date;
  appCoolTill?: Date;
  appStatus?: string;
  appImagesList?: string;
  appVersion?: string;
  appBuyurl?: string;
  appLicense?: string;
  appContent?: string;
  appThumbnail?: { data: Media };
  appScreenshots?: { data: Media[] };
  appNewContent: BlocksContent;
  appFileSize: string;
  appFileName: string;
  catId: number;
  subCatId: number;
  id?: number;
  metaTitle?: string;
  metaDescription?: string;
};

export type appImages = {
  data: [
    {
      attributes: {
        alternativeText: String;
        caption: String;
        createdAt: String;
        ext: String;
        formats: Object;
        hash: String;
        height: Number;
        mime: String;
        name: String;
        previewUrl: String;
        provider: String;
        provider_metadata: String;
        size: Number;
        updatedAt: String;
        url: String;
        width: Number;
      };
      id: String;
    }
  ];
};

// INTERFACES

export interface iSubcategory {
  id: number;
  attributes: {
    createdAt: Date;
    updatedAt: Date;
    publishedAt?: Date;
    subcategoryName: string;
    parentCategory: number;
    subcategorySlug: string;
    subcategoryId?: string;
    category_list?: { data: iCategoryList };
    app?: { data: AppDetails };
  };
}

export interface iCategoryList {
  id: number;
  attributes: {
    createdAt: Date;
    updatedAt: Date;
    publishedAt?: Date;
    categoryName: string;
    slug: string;
    isChild?: boolean;
    hasChild?: boolean;
    subcategories: { data: iSubcategory[] };
    app?: { data: AppDetails };
    subcategoryCount?: number;
  };
}

export interface iAppTag {
  id: number;
  attributes: {
    createdAt: Date;
    updatedAt: Date;
    publishedAt?: Date;
    appTag?: string;
    app?: { data: AppDetails };
  };
}

export interface iAppType {
  id: number;
  attributes: {
    createdAt: Date;
    updatedAt: Date;
    publishedAt?: Date;
    appType?: string;
    app?: { data: AppDetails };
  };
}

export interface Media {
  id: number;
  attributes: {
    name: string;
    alternativeText: string;
    caption: string;
    width: number;
    height: number;
    formats: {
      thumbnail: MediaFormat;
      small: MediaFormat;
      medium: MediaFormat;
      large: MediaFormat;
    };
    hash: string;
    ext: string;
    mime: string;
    size: number;
    url: string;
    previewUrl: string;
    provider: string;
    createdAt: Date;
    updatedAt: Date;
  };
}

export interface MediaFormat {
  name: string;
  hash: string;
  ext: string;
  mime: string;
  width: number;
  height: number;
  size: number;
  path: string;
  url: string;
}
export interface iImpressionPayload {
  userId?: string;
  userClass?: string;
  source?: string;
  implementation?: string;
  event?: string;
  url?: string;
  subid?: string;
  subid2?: string;
  subid3?: string;
  subid4?: string;
  referrer?: string;
  country?: string;
  isp?: string;
  browser?: string;
  browser_version?: string;
  platform?: string;
  user_agent?: string;
}

export enum ImpressionEvent {
  PAGE_VIEW = "page_view",
  CLICK = "click",
  DOWNLOAD = "download",
}

export interface Application {
  id: number;
  attributes: {
    createdAt: Date;
    updatedAt: Date;
    publishedAt?: Date;
    name: string;
    slug: string;
    excerpt: string;
    description?: string;
    thumbnail?: { data: Media };
    screenshots?: { data: Media[] };
    license?: string;
    platform?: { data: Platform };
    publisher?: { data: Publisher };
    rating?: number;
    ratingCount?: number;
    category?: { data: Category };
    currentApplicationVersion?: { data: ApplicationVersion };
    applicationVersions: { data: ApplicationVersion[] };
    editorPick?: boolean;
    isAppEnabled?: boolean;
    downloads?: number;
    downloads30Days?: number;
    metaTitle?: string;
    metaDescription?: string;
  };
}

export interface CustomApplication {
  createdAt: Date;
  updatedAt: Date;
  publishedAt?: Date;
  name: string;
  slug: string;
  excerpt: string;
  description?: string;
  downloadNumber?: number;
  thumbnail?: {
    id: number;
    name: string;
    alternativeText: string;
    ext: string;
    url: string;
  };
  screenshots?: { data: Media[] };
  license?: string;
  platform?: {
    id: number;
    name: string;
  };
  publisher?: { data: Publisher };
  rating?: number;
  ratingCount?: number;
  id?: number;
  category?: {
    id: number;
    name: string;
    slug: string;
    parentId: number;
  };
  currentApplicationVersion?: { data: ApplicationVersion };
  applicationVersions: { data: ApplicationVersion[] };
  editorPick?: boolean;
  isAppEnabled?: boolean;
}

export interface Category {
  id: number;
  attributes: {
    createdAt: Date;
    updatedAt: Date;
    publishedAt?: Date;
    name?: string;
    internalName?: string;
    isParent: boolean;
    parentId?: number;
    slug: string;
  };
}

export interface ApplicationDownload {
  id: number;
  attributes: {
    createdAt: Date;
    updatedAt: Date;
    publishedAt?: Date;
    date: Date;
    count: number;
    applicationId?: number;
  };
}

export interface ApplicationVersion {
  id: number;
  attributes: {
    createdAt: Date;
    updatedAt: Date;
    publishedAt?: Date;
    version?: string;
    externalDownloadUrl?: string;
    size?: string;
    downloadFile?: string;
  };
}

export interface Platform {
  id: number;
  attributes: {
    createdAt: Date;
    updatedAt: Date;
    publishedAt?: Date;
    name?: string;
    slug: string;
  };
}

export interface Publisher {
  id: number;
  attributes: {
    createdAt: Date;
    updatedAt: Date;
    publishedAt?: Date;
    name?: string;
    description?: string;
    slug: string;
  };
}

export interface ArticleCategory {
  id: number;
  attributes: {
    createdAt: Date;
    updatedAt: Date;
    publishedAt?: Date;
    title: string;
    metaDescription?: string;
    slug: string;
    articles: { data: Article[] };
    metaKeywords?: string;
    metaTitle?: string;
  };
}

export interface Article {
  id: number;
  attributes: {
    createdAt: Date;
    updatedAt: Date;
    publishedAt?: Date;
    title: string;
    metaDescription: string;
    metaKeywords: string;
    slug: string;
    featuredImage?: { data: Media };
    content: any;
    subtitle?: string;
    isFeaturedArticle?: boolean;
    articleCategory?: { data: ArticleCategory };
    excerpt?: string;
    author?: { data: Author };
    readingTime?: number;
    metaTitle: string;
  };
}

export interface Author {
  id: number;
  attributes: {
    createdAt: Date;
    updatedAt: Date;
    publishedAt?: Date;
    name?: string;
    about?: string;
    avatar?: { data: Media };
    articles: { data: Article[] };
    metaKeywords?: string;
    metaDescription?: string;
    slug?: string;
  };
}

export interface Game {
  id: number;
  attributes: {
    createdAt: Date;
    updatedAt: Date;
    publishedAt?: Date;
    title: string;
    metaDescription?: string;
    metaKeywords?: string;
    slug: string;
    iframe?: string;
    game_categories?: { data: GameCategory[] };
    excerpt?: string;
    metaTitle?: string;
    cover?: { data: Media };
    gamePlays: number;
    squareIcon?: { data: Media };
  };
}

export interface GameCategory {
  id: number;
  attributes: {
    createdAt: Date;
    updatedAt: Date;
    publishedAt?: Date;
    title: string;
    metaDescription?: string;
    slug: string;
    metaKeywords?: string;
    metaTitle?: string;
    games: { data: Game[] };
  };
}

export type ServerContextProps = {
  userID: string;
  userClass: string;
  nonce: string;
  isDeveloperZone: boolean;
  inAppMode: boolean;
  device: {
    isMobile: boolean;
    isTablet: boolean;
    isDesktop: boolean;
    isBot: boolean;
    os: string;
    browser: string;
  };
  countryCode: string;
  platform: string;
  url: string;
  referrer: string | null;
};

export type ServerToClientProps = {
  urlProps?: any;
  data?: any;
  otherProps?: any;
};
